import React, { useEffect, useRef, useState } from 'react';
import I18n from 'i18n-js';

import { RCGuidePopover } from '@reverbdotcom/cadence/components';
import { getItem, setItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

import { useFindFavorite } from './find_favorite_context';

export const HOIST_FAVORITE_CALLOUT_KEY = 'hoist-favorite-callout';
const LOGGED_IN_DISMISS_EXPIRATION_DAYS = 90;
const LOGGED_OUT_DISMISS_EXPIRATION_DAYS = 7;

function daysToMs(days: number): number {
  return days * 24 * 60 * 60 * 1000;
}

export function HoistFavoriteCallout({ anchor }: { anchor: JSX.Element }) {
  const { canFavorite, favorite } = useFindFavorite();
  const user = useUser();
  const [calloutTimestampValue, setCalloutTimestampValue] = useState(getItem(HOIST_FAVORITE_CALLOUT_KEY) || '0');

  const currentTimestamp = Date.now();
  const calloutTimestamp = parseInt(calloutTimestampValue, 10);
  const msSinceDismissed = currentTimestamp - calloutTimestamp;

  const dismissExpirationDays = user.loggedOut ? LOGGED_OUT_DISMISS_EXPIRATION_DAYS : LOGGED_IN_DISMISS_EXPIRATION_DAYS;
  const userEligibleForPopover = msSinceDismissed > daysToMs(dismissExpirationDays);
  const isOpen = canFavorite && favorite.favorited === false && userEligibleForPopover;

  const setTimestamp = () => {
    const newTimestamp = Date.now();
    setItem(HOIST_FAVORITE_CALLOUT_KEY, newTimestamp.toString());
    setCalloutTimestampValue(newTimestamp.toString());
  };

  // When a logged-in user clicks the heart while the popover is open, favorite.favorited
  // flips to true — treat that as an implicit dismiss so the popover respects the cooldown.
  const prevFavoritedRef = useRef(favorite.favorited);
  useEffect(() => {
    if (prevFavoritedRef.current === false && favorite.favorited === true && userEligibleForPopover) {
      setTimestamp();
    }
    prevFavoritedRef.current = favorite.favorited;
  }, [favorite.favorited]);

  if (!canFavorite) return null;

  const title = I18n.t('discovery.favorites.hoistFavoriteCallout.title');
  const content = I18n.t('discovery.favorites.hoistFavoriteCallout.content');

  return (
    <RCGuidePopover
      title={title}
      content={content}
      isOpen={isOpen}
      width="narrow"
      onDismiss={setTimestamp}
      anchor={anchor}
      preventAutoFocus
    />
  );
}
